import type { AudioManager } from '../../audio/AudioManager';
import type { PointerEvent2, TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawBlade } from '../../render/WeaponRenderer';
import type { HeatMap, StrikeQuality, StrikeResult, WeaponDefinition } from '../../types';
import { addLocalHeat, cloneHeatMap, cool } from '../entities/Weapon';

const TOTAL_STRIKES = 5;
const BASE_PERFECT_MS = 60;
const BASE_GOOD_MS = 170;
const BLADE_COOL_PER_SEC = 0.05;
const SPATIAL_RADIUS = 38;
const COMBO_FOR_BONUS = 3;
const COMBO_BONUS_TIMING_MULT = 1.5;

// Hammer choreography (seconds).
const REST_MIN = 0.35;
const REST_MAX = 1.0;
const WINDUP_DURATION = 0.36;
const HOVER_DURATION = 0.16;
const FALL_DURATION = 0.32;
const IMPACT_LINGER = 0.12;
const RESET_DURATION = 0.18;

type HammerStage = 'rest' | 'windup' | 'hover' | 'fall' | 'impact' | 'reset';

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  color: [number, number, number];
}

interface ScreenShake {
  amount: number;
  decay: number;
}

export class StrikeSubphase {
  private def: WeaponDefinition;
  private audio: AudioManager;
  private heatMap: HeatMap;
  private strikes: StrikeResult[] = [];
  private done = false;
  private unbindDown: (() => void) | null = null;
  private timeSeconds = 0;
  private targetPosition = 0.5;
  private sparks: Spark[] = [];
  private shake: ScreenShake = { amount: 0, decay: 0 };
  private lastStrikeFlash = 0;
  private lastStrikeQuality: StrikeQuality | null = null;
  private comboStreak = 0;
  private bestComboStreak = 0;
  private comboFlash = 0;

  // Hammer state.
  private stage: HammerStage = 'rest';
  private stageT = 0; // time in current stage
  private stageDur = 0;
  /** Set to true when the player has already tapped during this strike. */
  private tappedThisStrike = false;
  /** Recorded tap timing error relative to impact, in ms (signed: negative = early). */
  private pendingTimingErrorMs: number | null = null;
  private pendingSpatialHit = false;

  constructor(
    def: WeaponDefinition,
    initialHeat: HeatMap,
    input: TouchInput,
    audio: AudioManager,
  ) {
    this.def = def;
    this.audio = audio;
    this.heatMap = cloneHeatMap(initialHeat);
    this.advanceTarget(0);
    this.enterStage('rest');
    this.unbindDown = input.onPointerDown((e) => this.handleTap(e));
  }

  private enterStage(stage: HammerStage): void {
    this.stage = stage;
    this.stageT = 0;
    switch (stage) {
      case 'rest':
        // Procedural rhythm gap. Slight bias toward shorter gaps over time so
        // the phase doesn't drag on. Difficulty squeezes the range.
        this.stageDur = REST_MIN + Math.random() * (REST_MAX - REST_MIN) * (1 - this.def.strikeDifficulty * 0.3);
        this.tappedThisStrike = false;
        this.pendingTimingErrorMs = null;
        this.pendingSpatialHit = false;
        break;
      case 'windup':
        this.stageDur = WINDUP_DURATION;
        break;
      case 'hover':
        this.stageDur = HOVER_DURATION;
        break;
      case 'fall':
        this.stageDur = FALL_DURATION;
        break;
      case 'impact':
        this.stageDur = IMPACT_LINGER;
        // Resolve the strike at the moment of impact.
        this.resolveStrike();
        break;
      case 'reset':
        this.stageDur = RESET_DURATION;
        break;
    }
  }

  private handleTap(e: PointerEvent2): void {
    if (this.done) return;
    if (this.tappedThisStrike) return; // one tap per strike

    const { ax, ay } = this.anvilCoords();
    const tx = ax + (this.targetPosition - 0.5) * this.bladeLength();
    const dx = e.pos.x - tx;
    const dy = e.pos.y - ay;
    const dist = Math.sqrt(dx * dx + dy * dy);
    this.pendingSpatialHit = dist <= SPATIAL_RADIUS;

    // Compute time-from-impact in ms. Impact happens at the end of the 'fall' stage.
    const tToImpact = this.timeUntilImpact();
    // Negative = tapped before impact, positive = after.
    const errorMs = -tToImpact * 1000;
    this.pendingTimingErrorMs = errorMs;
    this.tappedThisStrike = true;
  }

  /** Seconds remaining until the hammer reaches the anvil. Negative if it already has. */
  private timeUntilImpact(): number {
    switch (this.stage) {
      case 'rest':
        return this.stageDur - this.stageT + WINDUP_DURATION + HOVER_DURATION + FALL_DURATION;
      case 'windup':
        return this.stageDur - this.stageT + HOVER_DURATION + FALL_DURATION;
      case 'hover':
        return this.stageDur - this.stageT + FALL_DURATION;
      case 'fall':
        return this.stageDur - this.stageT;
      case 'impact':
        return -this.stageT;
      case 'reset':
        // The next impact is far enough away that timing here is always a miss.
        return Number.POSITIVE_INFINITY;
    }
  }

  /** Resolves the current strike at impact moment using stored tap data. */
  private resolveStrike(): void {
    const windowMult = this.comboStreak >= COMBO_FOR_BONUS ? COMBO_BONUS_TIMING_MULT : 1;
    const perfectMs = BASE_PERFECT_MS * windowMult;
    const goodMs = BASE_GOOD_MS * windowMult;

    let quality: StrikeQuality;
    if (!this.tappedThisStrike || this.pendingTimingErrorMs === null) {
      quality = 'miss';
    } else if (!this.pendingSpatialHit) {
      quality = 'miss';
    } else {
      const errAbs = Math.abs(this.pendingTimingErrorMs);
      if (errAbs <= perfectMs) quality = 'perfect';
      else if (errAbs <= goodMs) quality = 'good';
      else quality = 'miss';
    }

    const errMs = this.pendingTimingErrorMs ?? 999;
    const result: StrikeResult = {
      quality,
      position: this.targetPosition,
      timingErrorMs: Math.abs(errMs),
    };
    this.strikes.push(result);
    this.updateCombo(quality);
    this.applyStrikeEffects(result);
  }

  private updateCombo(q: StrikeQuality): void {
    if (q === 'perfect') {
      this.comboStreak++;
      if (this.comboStreak > this.bestComboStreak) this.bestComboStreak = this.comboStreak;
      if (this.comboStreak >= COMBO_FOR_BONUS) this.comboFlash = 1;
    } else {
      this.comboStreak = 0;
    }
  }

  private applyStrikeEffects(r: StrikeResult): void {
    this.lastStrikeFlash = 1;
    this.lastStrikeQuality = r.quality;
    if (r.quality === 'perfect') {
      this.audio.play('strike_perfect');
      this.spawnSparks(r.position, 22, [255, 220, 130]);
      this.shake = { amount: 6, decay: 12 };
      addLocalHeat(this.heatMap, r.position, -0.18, 0.07);
    } else if (r.quality === 'good') {
      this.audio.play('strike_good');
      this.spawnSparks(r.position, 10, [255, 170, 80]);
      this.shake = { amount: 3, decay: 12 };
      addLocalHeat(this.heatMap, r.position, -0.13, 0.08);
    } else {
      this.audio.play('strike_miss');
      this.spawnSparks(r.position, 4, [120, 100, 80]);
      this.shake = { amount: 1.5, decay: 14 };
      addLocalHeat(this.heatMap, r.position, 0.05, 0.06);
    }
  }

  private spawnSparks(position: number, count: number, color: [number, number, number]): void {
    const { ax, ay } = this.anvilCoords();
    const localX = ax + (position - 0.5) * this.bladeLength();
    const localY = ay;
    for (let i = 0; i < count; i++) {
      const ang = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
      const speed = 90 + Math.random() * 140;
      this.sparks.push({
        x: localX + (Math.random() - 0.5) * 4,
        y: localY,
        vx: Math.cos(ang) * speed,
        vy: Math.sin(ang) * speed,
        life: 0.35 + Math.random() * 0.35,
        maxLife: 0.7,
        color,
      });
    }
  }

  private advanceTarget(strikeIndex: number): void {
    const r = Math.random();
    const candidates = [0.28, 0.42, 0.55, 0.7, 0.82];
    const idx = Math.min(candidates.length - 1, Math.floor(r * candidates.length));
    const prev = strikeIndex > 0 ? this.strikes[strikeIndex - 1]!.position : -1;
    let next = candidates[idx]!;
    if (Math.abs(next - prev) < 0.1) {
      next = candidates[(idx + 2) % candidates.length]!;
    }
    this.targetPosition = next;
  }

  update(dt: number): void {
    if (this.done) return;
    this.timeSeconds += dt;
    cool(this.heatMap, BLADE_COOL_PER_SEC * dt);

    // Sparks / shake / flashes.
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i]!;
      s.life -= dt;
      if (s.life <= 0) {
        this.sparks.splice(i, 1);
        continue;
      }
      s.vy += 220 * dt;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
    }
    if (this.shake.amount > 0) {
      this.shake.amount = Math.max(0, this.shake.amount - this.shake.decay * dt);
    }
    this.lastStrikeFlash = Math.max(0, this.lastStrikeFlash - dt * 3);
    this.comboFlash = Math.max(0, this.comboFlash - dt * 2);

    // Hammer choreography.
    this.stageT += dt;
    if (this.stageT >= this.stageDur) {
      switch (this.stage) {
        case 'rest':
          this.enterStage('windup');
          break;
        case 'windup':
          this.enterStage('hover');
          break;
        case 'hover':
          this.enterStage('fall');
          break;
        case 'fall':
          this.enterStage('impact');
          break;
        case 'impact':
          this.enterStage('reset');
          break;
        case 'reset':
          if (this.strikes.length >= TOTAL_STRIKES) {
            this.complete();
          } else {
            this.advanceTarget(this.strikes.length);
            this.enterStage('rest');
          }
          break;
      }
    }
  }

  isComplete(): boolean {
    return this.done;
  }

  getResult(): { strikes: StrikeResult[]; heatMap: HeatMap; bestComboStreak: number } {
    return { strikes: this.strikes, heatMap: this.heatMap, bestComboStreak: this.bestComboStreak };
  }

  private complete(): void {
    this.done = true;
    this.unbindDown?.();
    this.unbindDown = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const shakeX = (Math.random() - 0.5) * this.shake.amount;
    const shakeY = (Math.random() - 0.5) * this.shake.amount;
    ctx.save();
    ctx.translate(shakeX, shakeY);

    ctx.fillStyle = '#0a0908';
    ctx.fillRect(-10, -10, LOGICAL_WIDTH + 20, LOGICAL_HEIGHT + 20);

    ctx.fillStyle = '#e6d3a4';
    ctx.font = 'bold 20px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Strike the Blade', LOGICAL_WIDTH / 2, 18);
    ctx.fillStyle = '#a8957a';
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText(
      `Strike ${Math.min(this.strikes.length + 1, TOTAL_STRIKES)} of ${TOTAL_STRIKES}` +
        '   ·   Tap the glow as the hammer falls',
      LOGICAL_WIDTH / 2,
      44,
    );

    this.drawAnvil(ctx);
    drawBlade(ctx, this.def.shape, this.heatMap, {
      cx: LOGICAL_WIDTH / 2,
      cy: 380,
      length: this.bladeLength(),
      rotation: 0,
    });

    this.drawTarget(ctx);
    this.drawHammer(ctx);

    if (this.lastStrikeFlash > 0 && this.lastStrikeQuality) {
      const alpha = this.lastStrikeFlash * 0.5;
      const color =
        this.lastStrikeQuality === 'perfect'
          ? `rgba(255,240,140,${alpha})`
          : this.lastStrikeQuality === 'good'
            ? `rgba(255,180,80,${alpha})`
            : `rgba(120,100,80,${alpha})`;
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);
    }

    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (const s of this.sparks) {
      const lifeT = s.life / s.maxLife;
      const a = Math.max(0, lifeT);
      const [r, g, b] = s.color;
      ctx.fillStyle = `rgba(${r},${g},${b},${a})`;
      ctx.fillRect(s.x - 1, s.y - 1, 2, 2);
    }
    ctx.restore();

    this.drawStrikeDots(ctx);
    this.drawComboBadge(ctx);

    ctx.restore();
  }

  private bladeLength(): number {
    return 300;
  }

  private anvilCoords(): { ax: number; ay: number } {
    return { ax: LOGICAL_WIDTH / 2, ay: 380 };
  }

  private drawAnvil(ctx: CanvasRenderingContext2D): void {
    const { ax, ay } = this.anvilCoords();
    const topW = 260;
    const topH = 14;
    ctx.fillStyle = '#2c2a2e';
    ctx.fillRect(ax - topW / 2, ay + 18, topW, topH);
    ctx.fillStyle = '#1f1d20';
    ctx.beginPath();
    ctx.moveTo(ax - topW / 2 + 30, ay + 18 + topH);
    ctx.lineTo(ax + topW / 2 - 30, ay + 18 + topH);
    ctx.lineTo(ax + topW / 2 - 60, ay + 18 + topH + 50);
    ctx.lineTo(ax - topW / 2 + 60, ay + 18 + topH + 50);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#2c2a2e';
    ctx.fillRect(ax - topW / 2 + 10, ay + 18 + topH + 50, topW - 20, 16);
    ctx.strokeStyle = '#0a090a';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(ax - topW / 2, ay + 18, topW, topH);
  }

  private drawTarget(ctx: CanvasRenderingContext2D): void {
    const { ax, ay } = this.anvilCoords();
    const tx = ax + (this.targetPosition - 0.5) * this.bladeLength();
    // Brighten the glow when hammer is mid-windup/hover/fall to draw the eye.
    const armed = this.stage === 'windup' || this.stage === 'hover' || this.stage === 'fall';
    const baseAlpha = armed ? 0.75 : 0.45;
    const pulse = 0.5 + 0.5 * Math.sin(this.timeSeconds * 8);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const grad = ctx.createRadialGradient(tx, ay, 1, tx, ay, 26);
    grad.addColorStop(0, `rgba(255, 230, 120, ${baseAlpha + 0.25 * pulse})`);
    grad.addColorStop(1, 'rgba(255, 230, 120, 0)');
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(tx, ay, 26, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.strokeStyle = 'rgba(255, 220, 120, 0.25)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(tx, ay, SPATIAL_RADIUS, 0, Math.PI * 2);
    ctx.stroke();

    ctx.fillStyle = '#ffe488';
    ctx.beginPath();
    ctx.moveTo(tx, ay - 38);
    ctx.lineTo(tx - 6, ay - 50);
    ctx.lineTo(tx + 6, ay - 50);
    ctx.closePath();
    ctx.fill();
  }

  /** Progress 0-1 of the current stage. */
  private stageProgress(): number {
    if (this.stageDur <= 0) return 1;
    return Math.min(1, this.stageT / this.stageDur);
  }

  /** Returns the current handle angle (radians, world frame, +x = right, +y = down)
   *  for a given impact angle. */
  private hammerAngle(impactAngle: number): number {
    // Lift the head over the strike point during windup (angle increases past
    // impact, rotating the head from down-left to up-left of the pivot).
    const windupAngle = impactAngle + 1.3;
    const restAngle = impactAngle + 0.65;
    switch (this.stage) {
      case 'rest':
        return restAngle;
      case 'windup':
        return restAngle + (windupAngle - restAngle) * easeOutCubic(this.stageProgress());
      case 'hover':
        return windupAngle;
      case 'fall':
        return windupAngle + (impactAngle - windupAngle) * easeInQuad(this.stageProgress());
      case 'impact':
        return impactAngle;
      case 'reset':
        return impactAngle + (restAngle - impactAngle) * easeOutCubic(this.stageProgress());
    }
  }

  private drawHammer(ctx: CanvasRenderingContext2D): void {
    const { ax, ay } = this.anvilCoords();
    const tx = ax + (this.targetPosition - 0.5) * this.bladeLength();

    // Smith's grip is up-and-right of the target, giving a shallow handle angle.
    const pivotX = tx + 140;
    const pivotY = ay - 80;

    const dxImpact = tx - pivotX;
    const dyImpact = ay - pivotY;
    const distToTarget = Math.sqrt(dxImpact * dxImpact + dyImpact * dyImpact);
    const impactAngle = Math.atan2(dyImpact, dxImpact);

    // Head proportions: chunky along the handle, slightly less perpendicular.
    const headThicknessAlongHandle = 26;
    const headPerpHalf = 16;
    const handleLen = distToTarget - headThicknessAlongHandle / 2;
    const angle = this.hammerAngle(impactAngle);

    // Smith's grip knob — only drawn if it lands on the canvas.
    if (pivotX > 0 && pivotX < LOGICAL_WIDTH) {
      ctx.fillStyle = '#2a1a0e';
      ctx.beginPath();
      ctx.arc(pivotX, pivotY, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#0a0504';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    ctx.save();
    ctx.translate(pivotX, pivotY);
    ctx.rotate(angle);

    // Wooden handle.
    const handleThickness = 7;
    ctx.fillStyle = '#9a6a3a';
    ctx.fillRect(0, -handleThickness / 2, handleLen, handleThickness);
    // Wood grain (a few darker streaks along the length).
    ctx.fillStyle = 'rgba(60, 32, 14, 0.55)';
    ctx.fillRect(0, -handleThickness / 2 + 1, handleLen, 0.8);
    ctx.fillRect(0, 1, handleLen, 0.8);
    // Side outline.
    ctx.strokeStyle = '#3a2010';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, -handleThickness / 2, handleLen, handleThickness);
    // Top highlight.
    ctx.fillStyle = 'rgba(255, 225, 170, 0.18)';
    ctx.fillRect(3, -handleThickness / 2, handleLen - 6, 1.5);

    // Hammer head: chunky block at the far end of the haft.
    const hx = handleLen;
    ctx.fillStyle = this.stage === 'impact' ? '#5a5550' : '#363636';
    ctx.fillRect(hx - headThicknessAlongHandle / 2, -headPerpHalf, headThicknessAlongHandle, headPerpHalf * 2);
    ctx.strokeStyle = '#080706';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(hx - headThicknessAlongHandle / 2, -headPerpHalf, headThicknessAlongHandle, headPerpHalf * 2);
    // Striking face (the +x cap — the leading edge that contacts the blade).
    ctx.fillStyle = 'rgba(255, 240, 210, 0.22)';
    ctx.fillRect(hx + headThicknessAlongHandle / 2 - 3, -headPerpHalf + 1, 2.5, headPerpHalf * 2 - 2);
    // Top bevel highlight.
    ctx.fillStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.fillRect(hx - headThicknessAlongHandle / 2, -headPerpHalf, headThicknessAlongHandle, 3);
    // Bottom shading.
    ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
    ctx.fillRect(hx - headThicknessAlongHandle / 2, headPerpHalf - 3, headThicknessAlongHandle, 3);
    // Tiny handle peg / wedge where the haft meets the head.
    ctx.fillStyle = '#8a5a2a';
    ctx.beginPath();
    ctx.arc(hx - headThicknessAlongHandle / 2 + 2, 0, 2, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Drop-shadow indicator on the anvil grows as the hammer approaches.
    if (this.stage === 'fall' || this.stage === 'hover') {
      const windupAngle = impactAngle + 1.3;
      const span = impactAngle - windupAngle; // negative
      const progress = span === 0 ? 1 : (angle - windupAngle) / span;
      const t = Math.max(0, Math.min(1, progress));
      ctx.fillStyle = `rgba(255, 220, 120, ${0.15 + t * 0.5})`;
      ctx.fillRect(tx - 14, ay - 2, 28, 4);
    }
  }

  private drawStrikeDots(ctx: CanvasRenderingContext2D): void {
    const y = 70;
    const spacing = 28;
    const startX = LOGICAL_WIDTH / 2 - (spacing * (TOTAL_STRIKES - 1)) / 2;
    for (let i = 0; i < TOTAL_STRIKES; i++) {
      const x = startX + i * spacing;
      const r = this.strikes[i];
      let color = '#3a3530';
      if (r?.quality === 'perfect') color = '#ffd84a';
      else if (r?.quality === 'good') color = '#d49040';
      else if (r?.quality === 'miss') color = '#604030';
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#1a1714';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
  }

  private drawComboBadge(ctx: CanvasRenderingContext2D): void {
    if (this.comboStreak < 2) return;
    const flash = this.comboFlash;
    const x = LOGICAL_WIDTH - 12;
    const y = 100;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillStyle = this.comboStreak >= COMBO_FOR_BONUS ? '#ffd84a' : '#d49040';
    ctx.font = `bold ${14 + flash * 6}px system-ui, sans-serif`;
    ctx.fillText(`${this.comboStreak}x`, x, y);
    ctx.fillStyle = '#a8957a';
    ctx.font = '10px system-ui, sans-serif';
    ctx.fillText(
      this.comboStreak >= COMBO_FOR_BONUS ? 'BONUS WINDOW' : 'COMBO',
      x,
      y + 18,
    );
  }
}

function easeInQuad(t: number): number {
  return t * t;
}

function easeOutCubic(t: number): number {
  const u = 1 - t;
  return 1 - u * u * u;
}
